import axios from "axios";
import { MRT_SortingState } from "material-react-table";
import {
  BankReportDTO,
  BankUniversityReportDTO,
  DashboardDTO,
  DashboardStudentStatsDTO,
  DashboardSumDTO,
  ScholarshipSummaryDTO,
  ScholarshipSummaryFilter,
  UniversityReportDTO,
  UniversityReportDTO2,
  UniversityReportSearchDTO,
} from "../../modules/dashboard/model";
import {
  AdjustmentProgressInfo,
  CreatedMark118DTO,
  FileDTO,
  Message,
  Program116DTO,
  Program116HistoryDTO,
} from "../../modules/messages/models";
import {
  AcademicLeaveReason,
  ExportType,
  FormulaDTO,
  MT102CreateDTO,
  OvpoData,
  Page,
  PaymentStatusFilter,
  PeriodDTO,
  RemarkType,
  ReportSummary,
  ReportPaymentList,
  RefundPdfPreview,
  ReportDTO,
  ReportSearchDTO,
  ReportSignDTO,
  ReportStateDTO,
  ReportStatus,
  ReportType,
  ReportUniversityStatusDTO,
  ScholarshipRecalculationResult,
  StudentDTO, StudentScholarshipDTO, StudentScholarshipSearchDTO,
  StudentGroupDTO,
  StudentSearchDTO,
  SupportingDocument,
  UniversityDTO,
  UniversityInfo, UniversityResponsibleDTO, UniversityResponsibleSearchDTO,
} from "../../modules/report/models";
import {
  AcademicDegreeDTO,
  CitizenshipDTO,
  GopDTO,
} from "../../modules/dictionaries/models";
import { LoginInfo } from "../../modules/auth/model";
import { EpvoStudentInfoResponse } from "../../modules/epvo-student/models";
import {
  GbdflPersonResponse,
  GbdflStoredPerson,
} from "../../modules/gbdfl/models";
import {
  KdpConsent,
  KdpSettings,
  KdpSmsAnswer,
  KdpSmsReply,
  KdpUserData,
} from "../../modules/kdp/models";
import { GbdulByBinResponse } from "../../modules/gbdul/models";
import { UnemployedStatusResponse } from "../../modules/unemployed/models";
import { NobdStudentResponse } from "../../modules/nobd/models";
import {
  IntegrationLogDetail,
  IntegrationLogListItem,
  IntegrationLogReportGroup,
  IntegrationLogSearch,
  IntegrationLogStateCounts,
} from "../../modules/shep-logs/models";
import {
  IntegrationCallLogDetail,
  IntegrationCallLogItem,
} from "../../modules/integration-logs/models";
import {
  ScholarshipCalculatorRequest,
  ScholarshipPreviewResult,
} from "../../modules/scholarship-calculator/models";
import {
  EsobesBatch,
  EsobesBatchStart,
  EsobesSend,
  EsobesSendPage,
  EsobesSendQuery,
  EsobesSettings,
} from "../../modules/esobes/models";
const gzsBaseUrl = "/gateway/gzs/api/v1";
const shepBaseUrl = "/gateway/integration-shep-service/api/v1";
const internalBusBaseUrl = "/gateway/internal-bus";
// const gzsBaseUrl = "http://localhost:8080/gateway/gzs/api/v1";
const accountBaseUrl = "/gateway/account-service/api/v1";
const accountantBaseUrl = "/gateway/accountant-service/api/v1";
const studentBaseUrl = "/gateway/student-service/api/v1";
const inboxBaseUrl = "/gateway/inbox-service/api/v1";
const reportsUrl = gzsBaseUrl + "/reports";
const adjustmentUrl = gzsBaseUrl + "/adjustments";
const universitiesUrl = gzsBaseUrl + "/universities";
const dictionariesUrl = gzsBaseUrl + "/dictionaries";
const certificatesUrl = gzsBaseUrl + "/certificates";
const studentsUrl = gzsBaseUrl + "/students";
const dashboardUrl = gzsBaseUrl + "/dashboard";
const signUrl = gzsBaseUrl + "/sign";
const accountantReportUrl = accountantBaseUrl + "/reports";
const mark118Url = studentBaseUrl + "/students/mark118";

function reportSearchParams(search?: ReportSearchDTO): string {
  if (!search) {
    return "";
  }
  let searchParam = "";
  if (search.reportId) {
    searchParam += `&reportId=${search.reportId}`;
  }
  if (search.name && search.name.length > 0) {
    searchParam += `&name=${encodeURIComponent(search.name)}`;
  }
  if (search.ministry) {
    searchParam += `&ministry=${search.ministry}`;
  }
  if (search.status) {
    searchParam += `&status=${search.status}`;
  }
  if (search.period?.period && search.period.day) {
    searchParam += `&period=${search.period.period}&day=${search.period.day}`;
  }
  if (search.period?.year) {
    searchParam += `&year=${search.period.year}`;
  }
  return searchParam;
}

function dashboardPeriodParams(period?: PeriodDTO) {
  const params = new URLSearchParams();
  if (period?.period && period.day) {
    params.set("period", period.period);
    params.set("day", String(period.day));
  }
  if (period?.year) {
    params.set("year", String(period.year));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

class HttpService {
  static getReportStudents(
    id: string,
    pagination: { pageIndex: number; pageSize: number },
    sort: MRT_SortingState = [{ id: "id", desc: true }],
    search?: StudentSearchDTO
  ) {
    const sortQueryString = sort
      .map(
        (sortItem) =>
          `sort=${sortItem.id === "fullname" ? "lastName" : sortItem.id},${sortItem.desc ? "desc" : "asc"
          }`
      )
      .join("&");
    const queryParam = `?page=${pagination.pageIndex}&size=${pagination.pageSize}&${sortQueryString}`;
    let searchParam = "";
    if (search) {
      if (search.lastName && search.lastName.length > 0) {
        searchParam = searchParam.concat("&lastName=", search.lastName);
      }
      if (search.firstName && search.firstName.length > 0) {
        searchParam = searchParam.concat("&firstName=", search.firstName);
      }
      if (search.iin && search.iin.length > 0) {
        searchParam = searchParam.concat("&iin=", encodeURIComponent(search.iin));
      }
      if (search.gop && search.gop.length > 0) {
        searchParam = searchParam.concat("&gop=", encodeURIComponent(search.gop));
      }
      if (search.fundingId != null) {
        searchParam = searchParam.concat("&fundingId=", String(search.fundingId));
      }
      if (search.degreeId != null) {
        searchParam = searchParam.concat("&degreeId=", String(search.degreeId));
      }
      if (search.paymentStatus) {
        searchParam = searchParam.concat(
          "&paymentStatus=",
          encodeURIComponent(search.paymentStatus)
        );
      }
      if (search.statusMessage) {
        searchParam = searchParam.concat(
          "&statusMessage=",
          encodeURIComponent(search.statusMessage)
        );
      }
      if (search.statusMessageBlank) {
        searchParam = searchParam.concat("&statusMessageBlank=true");
      }
      if (search.versionChange) {
        searchParam = searchParam.concat("&versionChange=", encodeURIComponent(search.versionChange));
      }
      if (search.remarkId) {
        searchParam = searchParam.concat("&remarkId=", encodeURIComponent(search.remarkId));
      }
      if (search.filters?.length > 0) {
        search.filters.forEach((filter) => {
          searchParam = searchParam.concat(`&${filter.value}=true`);
        });
      }
    }
    return axios.get<Page<StudentDTO>>(
      reportsUrl + `/${id}/students${queryParam}${searchParam}`
    );
  }
  static getReports(
    page: number = 0,
    pageSize: number = 20,
    sort: MRT_SortingState = [{ id: "id", desc: true }],
    search?: ReportSearchDTO
  ) {
    const sortQueryString = sort
      .map(
        (sortItem) => `sort=${sortItem.id},${sortItem.desc ? "desc" : "asc"}`
      )
      .join("&");
    const queryParam = `?page=${page}&size=${pageSize}&${sortQueryString}`;
    return axios.get<Page<ReportDTO>>(
      reportsUrl + `/${queryParam}${reportSearchParams(search)}`
    );
  }

  /** Для вкладки дашборда «По вузам»: отчёты по фильтру без полного списка. */
  static getReportUniversityStatuses(search?: ReportSearchDTO) {
    const params = reportSearchParams(search);
    return axios.get<ReportUniversityStatusDTO[]>(
      reportsUrl + `/university-statuses${params ? "?" + params.replace(/^&/, "") : ""}`
    );
  }

  static getDashboardData(period?: PeriodDTO) {
    return axios.get<DashboardDTO>(dashboardUrl + `/${dashboardPeriodParams(period)}`);
  }

  static getDashboardStudentStats(period: PeriodDTO) {
    return axios.get<DashboardStudentStatsDTO>(
      dashboardUrl + `/students${dashboardPeriodParams(period)}`
    );
  }

  static getScholarshipSummary(period: PeriodDTO, filter: ScholarshipSummaryFilter) {
    const params = new URLSearchParams();
    if (period.period && period.day) {
      params.set("period", period.period);
      params.set("day", String(period.day));
    }
    if (period.year) {
      params.set("year", String(period.year));
    }
    if (filter.degreeId != null) {
      params.set("degreeId", String(filter.degreeId));
    }
    if (filter.universityId != null) {
      params.set("universityId", String(filter.universityId));
    }
    if (filter.gop) {
      params.set("gop", filter.gop);
    }
    return axios.get<ScholarshipSummaryDTO>(
      dashboardUrl + `/scholarships?${params.toString()}`
    );
  }

  static getDashboardSumData(
    page: number = 0,
    pageSize: number = 20,
    period?: PeriodDTO
  ) {
    const periodQuery = dashboardPeriodParams(period).replace(/^\?/, "&");
    const queryParam = `?page=${page}&size=${pageSize}${periodQuery}`;
    return axios.get<DashboardSumDTO>(dashboardUrl + `/sum${queryParam}`);
  }

  static getReportById(id: string) {
    return axios.get<ReportDTO>(reportsUrl + "/" + id);
  }

  static deleteReport(id: string) {
    return axios.delete(reportsUrl + "/" + id);
  }

  static getReportByEpvoId(epvoReportId: number) {
    return axios.get<ReportDTO>(reportsUrl + "/external/" + epvoReportId);
  }

  static getReportState(id: string) {
    return axios.get<ReportStateDTO>(reportsUrl + `/${id}/status`);
  }

  static updateReportStatus(
    reportId: string,
    status: ReportStatus,
    mt102DTO?: MT102CreateDTO
  ) {
    return axios.put<ReportStateDTO>(
      reportsUrl + `/${reportId}/change-status?status=` + status,
      mt102DTO ?? {}
    );
  }

  static downloadReport(
    reportId: string,
    type: ExportType = ExportType.CSV,
    reportType: ReportType = ReportType.AdditionalСonsolidatedReport,
    fundingId?: number,
    excludeFundingId?: number
  ) {
    let url =
      reportsUrl +
      `/${reportId}/export?exportType=${type}&reportName=${reportType}`;
    if (fundingId != null) {
      url += `&fundingId=${fundingId}`;
    }
    if (excludeFundingId != null) {
      url += `&excludeFundingId=${excludeFundingId}`;
    }
    return axios.get(url, {
      responseType: "blob",
    });
  }

  static downloadReportsArchive(
    search?: ReportSearchDTO,
    exportType: ExportType = ExportType.CSV,
    reportType: ReportType = ReportType.AdditionalСonsolidatedReportFull
  ) {
    return axios.get(
      reportsUrl +
        `/export-archive?exportType=${exportType}&reportName=${reportType}${reportSearchParams(search)}`,
      {
        responseType: "blob",
      }
    );
  }

  static downloadMnvoSummary(year: number) {
    return axios.get(reportsUrl + `/export-mnvo-summary?year=${year}`, {
      responseType: "blob",
    });
  }

  static downloadReportZip(reportId: string, mark324?: boolean) {
    let url = reportsUrl + `/${reportId}/export-zip`;
    if (mark324 != null) {
      url += `?mark324=${mark324}`;
    }
    return axios.get(url, {
      responseType: "blob",
    });
  }

  static downloadReportMt102Zip(reportId: string, mark324?: boolean) {
    let url = reportsUrl + `/${reportId}/export-mt102-zip`;
    if (mark324 != null) {
      url += `?mark324=${mark324}`;
    }
    return axios.get(url, {
      responseType: "blob",
    });
  }

  static downloadReportMt102ToPay(reportId: string, dto: MT102CreateDTO, mark324?: boolean) {
    let url = reportsUrl + `/${reportId}/export-mt102-to-pay`;
    if (mark324 != null) {
      url += `?mark324=${mark324}`;
    }
    return axios.post(url, dto, {
      responseType: "blob",
    });
  }

  static downloadBankData(reportId: string) {
    return axios.get(reportsUrl + `/${reportId}/export-bank-data`, {
      responseType: "blob",
    });
  }

  static downloadPaymentList(reportId: string) {
    return axios.get(reportsUrl + `/${reportId}/export-payment-list`, {
      responseType: "blob",
    });
  }

  static getReportBankData(reportId: string) {
    return axios.get<ReportPaymentList>(reportsUrl + `/${reportId}/bank-data`);
  }

  static uploadCertificates(data: FormData) {
    return axios.post(certificatesUrl + `/upload`, data);
  }

  static downloadCertificate(id: string) {
    return axios.get(certificatesUrl + `/${id}`, {
      responseType: "blob",
    });
  }

  static getSupportingDocuments() {
    return axios.get<SupportingDocument[]>(
      dictionariesUrl + "/supporting-documents"
    );
  }

  static getPeriods() {
    return axios.get<PeriodDTO[]>(dictionariesUrl + "/periods");
  }

  static getAcademicLeaveReasons() {
    return axios.get<AcademicLeaveReason[]>(dictionariesUrl + "/reasons");
  }

  static getStudents(
    page: number = 0,
    pageSize: number = 20,
    search?: StudentSearchDTO
  ) {
    const query = new URLSearchParams();
    query.set("page", String(page));
    query.set("size", String(pageSize));
    const lastName = search?.lastName?.trim();
    const firstName = search?.firstName?.trim();
    const iin = search?.iin?.trim();
    const gop = search?.gop?.trim();
    if (lastName) query.set("lastName", lastName);
    if (firstName) query.set("firstName", firstName);
    if (iin) query.set("iin", iin);
    if (gop) query.set("gop", gop);
    if (search?.fundingId != null) query.set("fundingId", String(search.fundingId));
    if (search?.universityId != null) query.set("universityId", String(search.universityId));
    if (search?.year != null && search.year >= 1000 && search.year <= 9999) {
      query.set("year", String(search.year));
    }
    search?.periodIds?.forEach((id) => query.append("periodIds", String(id)));
    search?.filters?.forEach((filter) => query.set(filter.value, "true"));
    return axios.get<Page<StudentGroupDTO>>(`${studentsUrl}?${query.toString()}`);
  }
  static getEpvoStudentInfo(iin: string) {
    return axios.get<EpvoStudentInfoResponse>(
      `${internalBusBaseUrl}/epvo/student-info/${iin}`
    );
  }

  static listGbdflPersons() {
    return axios.get<GbdflStoredPerson[]>(`${internalBusBaseUrl}/gbdfl/persons`);
  }

  static requestKdpUserData(uin: string, personalDataName: string) {
    return axios.post<KdpUserData>(`${internalBusBaseUrl}/kdp/user-data`, {
      uin,
      personalDataName,
      waitMillis: 0,
    });
  }

  static getKdpUserData(uin: string, personalDataName: string, signal?: AbortSignal) {
    return axios.get<KdpUserData>(`${internalBusBaseUrl}/kdp/user-data`, {
      params: { uin, personalDataName },
      signal,
    });
  }

  static listKdpConsents(personalDataName: string, signal?: AbortSignal) {
    return axios.get<KdpConsent[]>(`${internalBusBaseUrl}/kdp/consents`, {
      params: { personalDataName },
      signal,
    });
  }

  static getKdpSettings(signal?: AbortSignal) {
    return axios.get<KdpSettings>(`${internalBusBaseUrl}/kdp/settings`, { signal });
  }

  static replyKdpSms(phone: string, answer: KdpSmsAnswer) {
    return axios.post<KdpSmsReply>(`${internalBusBaseUrl}/kdp/test/sms-reply`, { phone, answer });
  }

  static findGbdflPerson(iin: string, refresh = false) {
    return axios.post<GbdflPersonResponse>(`${internalBusBaseUrl}/gbdfl/person`, {
      iin,
      refresh,
    });
  }

  static findGbdulByBin(bin: string) {
    return axios.get<GbdulByBinResponse>(`${internalBusBaseUrl}/gbdul/by-bin/${bin}`);
  }

  static findUnemployedStatus(iin: string) {
    return axios.get<UnemployedStatusResponse>(`${internalBusBaseUrl}/unemployed/status/${iin}`);
  }

  static findNobdByIin(iin: string) {
    return axios.get<NobdStudentResponse>(
      `${internalBusBaseUrl}/nedb/university-student/${iin}`
    );
  }

  static getStudentScholarships(
      page: number = 0,
      pageSize: number = 20,
      search?: StudentScholarshipSearchDTO
  ) {
    let queryParams = `?page=${page}&size=${pageSize}`;
    return axios.post<Page<StudentScholarshipDTO>>(studentsUrl + '/scholarships' + queryParams, search);
  }

  static getStudent(reportId: string, studentId: string) {
    return axios.get<StudentDTO>(
      `${reportsUrl}/${reportId}/students/${studentId}`
    );
  }

  static getStudentInfo(studentId: string) {
    return axios.get<StudentDTO>(studentsUrl + `/${studentId}`);
  }

  static getRemarkTypes() {
    return axios.get<RemarkType[]>(dictionariesUrl + "/remarks");
  }

  static getCitizenships() {
    return axios.get<CitizenshipDTO[]>(dictionariesUrl + "/citizenships");
  }

  static getGops() {
    return axios.get<GopDTO[]>(dictionariesUrl + "/gops");
  }

  static getDegrees() {
    return axios.get<AcademicDegreeDTO[]>(dictionariesUrl + "/degrees");
  }

  static exportDictionary(
    dictionaryType: "citizenships" | "gops" | "degrees" | "universities"
  ) {
    return axios.get(dictionariesUrl + `/${dictionaryType}/export`, {
      responseType: "blob",
    });
  }

  static updateRemark(remark: any, reportId: string, studentId: string) {
    return axios.put(
      reportsUrl + `/${reportId}/${studentId}/add-remark`,
      remark
    );
  }

  static updateRefund(message: string, reportId: string, studentId: string) {
    return axios.put(reportsUrl + `/${reportId}/${studentId}/add-refund`, {
      message: message,
    });
  }

  static updateRefunds(iins: string, message: string, reportId: string) {
    return axios.put(reportsUrl + `/${reportId}/add-refunds`, {
      iins: iins,
      message: message,
    });
  }

  static getReportSummary(reportId: string) {
    return axios.get<ReportSummary>(reportsUrl + `/${reportId}/summary`);
  }

  static getPaymentStatusFilters(reportId: string) {
    return axios.get<PaymentStatusFilter[]>(
      reportsUrl + `/${reportId}/payment-status-filters`
    );
  }

  static previewRefundIins(reportId: string, iins: string) {
    return axios.post<RefundPdfPreview>(
      reportsUrl + `/${reportId}/refunds/preview`,
      { iins }
    );
  }

  static previewRefundPdfs(reportId: string, files: File[]) {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return axios.post<RefundPdfPreview>(
      reportsUrl + `/${reportId}/refunds/from-pdf`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  }

  static getUniversities() {
    return axios.get<UniversityDTO[]>(universitiesUrl, {
      headers: { "Content-Type": "application/json" },
    });
  }

  static getUniversitiesPage(page: number = 0, pageSize: number = 20) {
    return axios.get<UniversityDTO[]>(
      universitiesUrl + `/pageable?page=${page}&size=${pageSize}`
    );
  }

  static getUniversity(id: string) {
    return axios.get<UniversityInfo>(universitiesUrl + `/${id}`);
  }

  static createUniversity(dto: UniversityDTO) {
    return axios.post<UniversityDTO>(universitiesUrl, dto);
  }

  static updateUniversity(id: string, dto: UniversityDTO) {
    return axios.put<UniversityDTO>(universitiesUrl + `/${id}`, dto);
  }

  static deleteUniversity(id: string) {
    return axios.delete(universitiesUrl + `/${id}`);
  }

  static importUniversities(data: FormData) {
    return axios.post<UniversityDTO[]>(universitiesUrl + "/import", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static uploadUniversityReport(data: FormData) {
    return axios.post<ReportStateDTO>(
      reportsUrl + "/upload/university-report",
      data,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  }

  static uploadUniversityManualReport(year: number, reportType: string, data: FormData) {
    return axios.post<ReportStateDTO>(
        reportsUrl + `/upload/university-manual-report/${year}/${reportType}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
    );
  }

  static getSignXml(reportId: string) {
    return axios.get<ReportSignDTO>(signUrl + `/info?reportId=${reportId}`);
  }

  static putSignXml(signId: string, data: string) {
    return axios.put(signUrl + `/${signId}`, { sign: data });
  }

  static rejectAdjustment(adjustmenId: string) {
    return axios.put(signUrl + `/${adjustmenId}/reject`);
  }

  static addAdjustment(
    reportId: string,
    studentId: string,
    formData: FormData
  ) {
    return axios.put<ReportStateDTO>(
      reportsUrl + `/${reportId}/${studentId}/add-adjustment`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
  }

  static secureController() {
    return axios.get("http://89.218.121.52/api/home/secure");
  }

  static getUniversityBankReport(
    page: number = 0,
    pageSize: number = 20,
    searchDTO: UniversityReportSearchDTO
  ) {
    let searchString = `exportType=${searchDTO.exportType}`;
    if (searchDTO.period) {
      searchString = searchString.concat(
        `&period=${searchDTO.period}&day=${searchDTO.day}&year=${searchDTO.year}`
      );
    }
    const queryParam = `page=${page}&size=${pageSize}&${searchString}`;
    return axios.get<Page<UniversityReportDTO>>(
      accountantReportUrl + `/university-data?${queryParam}`
    );
  }

  static getUniversityBankReport2(
    page: number = 0,
    pageSize: number = 20,
    searchDTO: UniversityReportSearchDTO
  ) {
    let searchString = `exportType=${searchDTO.exportType}`;
    if (searchDTO.period) {
      searchString = searchString.concat(
        `&period=${searchDTO.period}&day=${searchDTO.day}&year=${searchDTO.year}`
      );
    }
    if (searchDTO.universityName?.length > 0) {
      searchString = searchString.concat(
        `&universityName=${searchDTO.universityName}`
      );
    }
    const queryParam = `page=${page}&size=${pageSize}&${searchString}`;
    // return axios.get<Page<UniversityReportDTO2>>(accountantReportUrl + `/university-data-v2?${queryParam}`);
    return axios.get<Page<UniversityReportDTO2>>(reportsUrl + `/university-data-v2?${queryParam}`);
  }

  static getBankUniversityReport(
    page: number = 0,
    pageSize: number = 20,
    searchDTO: UniversityReportSearchDTO
  ) {
    let searchString = `exportType=${searchDTO.exportType}`;
    if (searchDTO.period) {
      searchString = searchString.concat(
        `&period=${searchDTO.period}&day=${searchDTO.day}&year=${searchDTO.year}`
      );
    }
    const queryParam = `page=${page}&size=${pageSize}&${searchString}`;
    return axios.get<Page<BankReportDTO>>(
      accountantReportUrl + `/bank-data?${queryParam}`
    );
  }

  static getBankUniversityReport2(
    page: number = 0,
    pageSize: number = 20,
    searchDTO: UniversityReportSearchDTO
  ) {
    let searchString = `exportType=${searchDTO.exportType}`;
    if (searchDTO.period) {
      searchString = searchString.concat(
        `&period=${searchDTO.period}&day=${searchDTO.day}&year=${searchDTO.year}`
      );
    }
    const queryParam = `page=${page}&size=${pageSize}&${searchString}`;
    // return axios.get<Page<BankUniversityReportDTO>>(accountantReportUrl + `/bank-data-v2?${queryParam}`);
    return axios.get<Page<BankUniversityReportDTO>>(reportsUrl + `/bank-data-v2?${queryParam}`);
  }

  static downloadAccountantReport(
    exportType: "BANK" | "UNIVERSITY",
    period?: PeriodDTO
  ) {
    let queryString = `exportType=${exportType}`;
    if (period) {
      queryString = queryString.concat(
        `&period=${period.period}&day=${period.day}`
      );
    }
    if (period?.year) {
      queryString = queryString.concat(`&year=${period.year}`);
    }

    return axios.get(accountantReportUrl + `/export?${queryString}`, {
      responseType: "blob",
    });
  }

  static getLoginInfo() {
    return axios
      .get<LoginInfo>(accountBaseUrl + '/events');
  }

  static getFullFormula(reportId: string, studentId: string) {
    return axios.get<FormulaDTO>(
      reportsUrl + `/${reportId}/${studentId}/full-formula`
    );
  }

  static getOvpoData(reportId: string) {
    return axios.get<OvpoData>(reportsUrl + `/${reportId}/export-to-file`);
  }

  static getOvpoStatus(fileId: string) {
    return axios.get<OvpoData>(reportsUrl + `/${fileId}/export-to-file/status`);
  }

  static getOvpoFile(fileId: string) {
    return axios.get<OvpoData>(reportsUrl + `/${fileId}/export-to-file/download`, {
      responseType: "blob",
    });
  }

  static bulkAdjustment(formData: FormData) {
    return axios.post<ReportStateDTO>(adjustmentUrl + "/bulk-parse", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static getAdjustmenInfo(id: string) {
    return axios.get<AdjustmentProgressInfo>(adjustmentUrl + `/${id}`);
  }

  static createMark118(markInfo: Program116DTO) {
    return axios.post<CreatedMark118DTO>(mark118Url, markInfo);
  }

  static checkMark118(formData: FormData) {
    return axios.post<string[]>(mark118Url + `/absent`, formData);
  }

  static signMark118Spec(processId: string, sign: string) {
    return axios.post<CreatedMark118DTO>(
      mark118Url + `/${processId}/signature`,
      sign,
      { headers: { "Content-Type": "application/xml" } }
    );
  }

  static signMark118DeptHead(processId: string, sign: string) {
    return axios.post<CreatedMark118DTO>(
      mark118Url + `/${processId}/dephead/approve`,
      sign,
      { headers: { "Content-Type": "application/xml" } }
    );
  }

  static rejectMark118DeptHead(processId: string) {
    return axios.put<CreatedMark118DTO>(
      mark118Url + `/${processId}/dephead/decline`
    );
  }

  static getSignMark118SSpec(processId: string) {
    return axios.get<string>(mark118Url + `/${processId}/signature`);
  }

  static getMark118History(studentIin: string) {
    return axios.post<Program116HistoryDTO[]>(mark118Url + "/history", {
      iin: studentIin,
    });
  }

  static getSignMark118SSpecSingle(
    processId: string,
    studentIin: string,
    isMark118: boolean
  ) {
    return axios.post<string>(
      mark118Url + `/manual_processes/${processId}/xml`,
      {
        mark118: isMark118 !== undefined ? isMark118 : false,
        iin: studentIin,
      }
    );
  }

  static signMark118DeptHeadSingle(processId: string, sign: string) {
    return axios.post<CreatedMark118DTO>(
      mark118Url + `/manual_processes/${processId}/dephead/approve`,
      sign,
      { headers: { "Content-Type": "application/xml" } }
    );
  }

  static uploadMark118File(processId: string, formData: FormData) {
    return axios.post<FileDTO>(mark118Url + `/${processId}/iins`, formData);
  }

  static uploadMark118BaseFiles(processId: string, formData: FormData) {
    return axios.post<FileDTO[]>(mark118Url + `/${processId}/files`, formData);
  }

  static downloadMark118Files(processId: string, fileId: string) {
    return axios.get(mark118Url + `/${processId}/files/${fileId}/download`, {
      responseType: "blob",
    });
  }

  static getIncomingMessages(
    pagination: {
      pageIndex: number;
      pageSize: number;
    },
    sort: MRT_SortingState = [{ id: "id", desc: true }]
  ) {
    const sortQueryString = sort
      .map(
        (sortItem) =>
          `sort=${sortItem.id === "fullname" ? "lastName" : sortItem.id},${sortItem.desc ? "desc" : "asc"
          }`
      )
      .join("&");
    const queryParam = `?page=${pagination.pageIndex}&size=${pagination.pageSize}&${sortQueryString}`;
    return axios.get<Page<Message>>(
      inboxBaseUrl + `/inbox/incoming${queryParam}`
    );
  }

  static getOutgoingMessages(
    pagination: {
      pageIndex: number;
      pageSize: number;
    },
    sort: MRT_SortingState = [{ id: "id", desc: true }]
  ) {
    const sortQueryString = sort
      .map(
        (sortItem) =>
          `sort=${sortItem.id === "fullname" ? "lastName" : sortItem.id},${sortItem.desc ? "desc" : "asc"
          }`
      )
      .join("&");
    const queryParam = `?page=${pagination.pageIndex}&size=${pagination.pageSize}&${sortQueryString}`;
    return axios.get<Page<Message>>(
      inboxBaseUrl + `/inbox/outgoing${queryParam}`
    );
  }

  static downloadStudentScholarshipReferenceXls(
      search: StudentScholarshipSearchDTO,
      studentScholarshipDTO: StudentScholarshipDTO
  ) {
    if (search) {
      studentScholarshipDTO['startDate'] = search.startDate;
      studentScholarshipDTO['endDate'] = search.endDate;
    }
    return axios.post(gzsBaseUrl + `/reference/scholarship/certificate/generate`, studentScholarshipDTO, {
      responseType: "blob",
    });
  }


  static getUniversityResponsibleList(
      page: number = 0,
      pageSize: number = 20,
      search?: UniversityResponsibleSearchDTO
  ) {
    let queryParams = `?page=${page}&size=${pageSize}`;
    return axios.post<Page<UniversityResponsibleDTO>>(gzsBaseUrl + '/universities/responsible/pageable' + queryParams, search);
  }

  static getAllSpecialistUserList() {
    return axios.get<StudentScholarshipDTO[]>(gzsBaseUrl + '/universities/responsible/all/specialist/users');
  }

  static saveUniversityResponsible(dto: UniversityResponsibleDTO) {
    return axios.post<UniversityResponsibleDTO>(gzsBaseUrl + '/universities/responsible/save', dto);
  }

  static getIntegrationLogGroups(
    page: number = 0,
    pageSize: number = 20,
    search?: IntegrationLogSearch
  ) {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("size", String(pageSize));
    if (search?.serviceId) {
      params.set("serviceId", search.serviceId);
    }
    if (search?.reportId) {
      params.set("reportId", search.reportId);
    }
    if (search?.universityId) {
      params.set("universityId", search.universityId);
    }
    if (search?.direction) {
      params.set("direction", search.direction);
    }
    if (search?.state) {
      params.set("state", search.state);
    }
    return axios.get<Page<IntegrationLogReportGroup>>(
      `${shepBaseUrl}/integration-logs/grouped?${params.toString()}`
    );
  }

  static getIntegrationLogStateCounts(search?: IntegrationLogSearch) {
    const params = new URLSearchParams();
    if (search?.serviceId) params.set("serviceId", search.serviceId);
    if (search?.reportId) params.set("reportId", search.reportId);
    if (search?.universityId) params.set("universityId", search.universityId);
    if (search?.direction) params.set("direction", search.direction);
    return axios.get<IntegrationLogStateCounts>(
      `${shepBaseUrl}/integration-logs/grouped/counts?${params.toString()}`
    );
  }

  static getIntegrationLog(id: number) {
    return axios.get<IntegrationLogDetail>(
      `${shepBaseUrl}/integration-logs/${id}`
    );
  }

  static getIntegrationLogsByReport(reportId: number) {
    return axios.get<IntegrationLogListItem[]>(
      `${shepBaseUrl}/integration-logs/report/${reportId}`
    );
  }

  static resendIntegrationLog(id: number) {
    return axios.post(`${shepBaseUrl}/integration-logs/${id}/resend`);
  }

  static resendReportToEpvo(
    reportId: string,
    kind: "STATUS" | "CALCULATION" | "PAYMENT"
  ) {
    return axios.post(
      `${gzsBaseUrl}/system/reports/${reportId}/resend-to-epvo?kind=${kind}`
    );
  }

  static finishPaid(reportId: string) {
    return axios.post(`${gzsBaseUrl}/system/reports/${reportId}/finish-paid`);
  }

  static calculateScholarshipPreview(payload: ScholarshipCalculatorRequest) {
    return axios.post<ScholarshipPreviewResult>(
      gzsBaseUrl + "/scholarship-calculator",
      payload
    );
  }

  static getIntegrationCallLogs(params: {
    page: number;
    size: number;
    serviceId?: string;
    success?: boolean;
    from?: string;
    to?: string;
    subject?: string;
  }) {
    const query = new URLSearchParams();
    query.set("page", String(params.page));
    query.set("size", String(params.size));
    if (params.serviceId) query.set("serviceId", params.serviceId);
    if (params.success != null) query.set("success", String(params.success));
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.subject) query.set("subject", params.subject);
    return axios.get<Page<IntegrationCallLogItem>>(
      `${internalBusBaseUrl}/api/v1/integration-logs?${query.toString()}`
    );
  }

  static getIntegrationCallLog(id: string) {
    return axios.get<IntegrationCallLogDetail>(
      `${internalBusBaseUrl}/api/v1/integration-logs/${id}`
    );
  }

  static recalculateScholarships(reportId: string) {
    return axios.post<ScholarshipRecalculationResult>(
      reportsUrl + `/${reportId}/recalculate-scholarships`,
      {},
      { timeout: 180000 }
    );
  }

  static startEsobesTransfer(reportId: string) {
    return axios.post<EsobesBatchStart>(`${reportsUrl}/${reportId}/esobes`);
  }

  static getEsobesSettings(signal?: AbortSignal) {
    return axios.get<EsobesSettings>(`${internalBusBaseUrl}/esobes/mgp/settings`, { signal });
  }

  static getEsobesBatch(batchId: string, signal?: AbortSignal) {
    return axios.get<EsobesBatch>(
      `${internalBusBaseUrl}/esobes/mgp/batches/${encodeURIComponent(batchId)}`,
      { signal }
    );
  }

  static getEsobesSends(query: EsobesSendQuery, signal?: AbortSignal) {
    const params = new URLSearchParams();
    if (query.reportId) params.set("reportId", query.reportId);
    if (query.status) params.set("status", query.status);
    if (query.iin) params.set("iin", query.iin);
    params.set("page", String(query.page));
    params.set("size", String(query.size));
    return axios.get<EsobesSendPage>(
      `${internalBusBaseUrl}/esobes/mgp/sends?${params.toString()}`,
      { signal }
    );
  }

  static retryEsobesSend(id: string) {
    return axios.post<EsobesSend>(
      `${internalBusBaseUrl}/esobes/mgp/sends/${encodeURIComponent(id)}/retry`
    );
  }
}

export default HttpService;
